// src/pages/MyKitsListPage.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ExternalLink, Pencil, Trash2, Eye, EyeOff, Loader2 } from 'lucide-react';
import { mediaKitApi } from '../services/mediaKitApi';

export default function MyKitsListPage() {
    const navigate = useNavigate();
    const [kits, setKits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        mediaKitApi.listMine()
            .then(({ kits }) => setKits(kits || []))
            .finally(() => setLoading(false));
    }, []);

    const handleDelete = async (kitId) => {
        if (!window.confirm('Delete this media kit permanently? This cannot be undone.')) return;
        setDeletingId(kitId);
        try {
            await mediaKitApi.remove(kitId);
            setKits((k) => k.filter((x) => x.id !== kitId));
        } catch (e) {
            alert(e.message);
        } finally {
            setDeletingId(null);
        }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading your media kits…</div>;
    }

    return (
        <div className="min-h-screen bg-slate-50 py-10 px-4 sm:px-6">
            <div className="max-w-5xl mx-auto">
                <div className="flex items-center justify-between mb-8">
                    <h1 className="text-2xl font-bold text-slate-800">Your media kits</h1>
                    <button
                        onClick={() => navigate('/aip/media-kit/new')}
                        className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                    >
                        <Plus size={16} /> New kit
                    </button>
                </div>

                {kits.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
                        <p className="text-slate-500 mb-4">You haven't created a media kit yet.</p>
                        <button
                            onClick={() => navigate('/aip/media-kit/new')}
                            className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                        >
                            <Plus size={16} /> Create your first kit
                        </button>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {kits.map((kit) => (
                            <div key={kit.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                                <div className="p-5">
                                    <div className="flex items-start justify-between gap-2 mb-3">
                                        <div className="min-w-0">
                                            <p className="font-bold text-slate-800 truncate">{kit.display_name || 'Untitled kit'}</p>
                                            <p className="text-xs text-slate-400 font-mono truncate">/kit/{kit.slug}</p>
                                        </div>
                                        <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-1 rounded-full ${kit.is_published ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                                            }`}>
                                            {kit.is_published ? <Eye size={11} /> : <EyeOff size={11} />}
                                            {kit.is_published ? 'Live' : 'Draft'}
                                        </span>
                                    </div>

                                    {kit.tagline && <p className="text-sm text-slate-500 mb-2 line-clamp-2">{kit.tagline}</p>}

                                    <div
                                        className="inline-flex items-center gap-1 text-xs text-slate-400 mb-4"
                                        title="Watch Count"
                                    >
                                        <Eye size={13} />
                                        <span>{kit.view_count ?? 0} views</span>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => navigate(`/aip/media-kit/${kit.id}`)}
                                            className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50"
                                        >
                                            <Pencil size={13} /> Edit
                                        </button>
                                        {kit.is_published && (
                                            <a
                                                href={`/kit/${kit.slug}`} target="_blank" rel="noreferrer"
                                                className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50"
                                            >
                                                <ExternalLink size={13} /> View
                                            </a>
                                        )}
                                        <button
                                            onClick={() => handleDelete(kit.id)}
                                            disabled={deletingId === kit.id}
                                            className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 text-slate-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-50 shrink-0"
                                        >
                                            {deletingId === kit.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}